import type { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Icon, type IconName } from '@components/atoms/Icon'
import { Typography } from '@components/atoms/Typography'
import styles from './BottomFloatingNav.module.scss'

export type NavItemId = 'home' | 'inbox' | 'requests'

export interface NavItem {
  id: NavItemId
  label: string
  icon: IconName
  href: string
  /**
   * When `true`, a red unread-notification dot overlays the icon. Typically
   * driven by the inbox tab when `notifications.unreadCount > 0`. Rendered
   * as an HTML overlay (no icon swap).
   */
  hasUnread?: boolean
}

export interface BottomFloatingNavProps {
  items: NavItem[]
  /**
   * Explicit active tab. When omitted, the component falls back to
   * `useLocation().pathname` and matches the longest-prefix `href`
   * (so `/inbox/123` correctly resolves to `inbox` and never to `/`).
   */
  activeId?: NavItemId
  /**
   * Optional render slot positioned to the right of the pill at the same
   * vertical baseline. Currently used for the `OroAiButton` entry-point;
   * keeps the nav presentational so domain-aware buttons live in
   * AppShell instead of here.
   */
  trailingAction?: ReactNode
  className?: string
}

function useResolvedActiveId(
  items: NavItem[],
  explicit: NavItemId | undefined,
): NavItemId | null {
  const location = useLocation()
  if (explicit) return explicit
  if (items.length === 0) return null

  // Match the longest `href` first so `/inbox/123` lands on `inbox`, not on
  // the root `/` home item (which would otherwise startsWith every path).
  const sorted = [...items].sort((a, b) => b.href.length - a.href.length)
  for (const item of sorted) {
    if (location.pathname === item.href) return item.id
    if (location.pathname.startsWith(`${item.href}/`)) return item.id
  }
  return items[0].id
}

export function BottomFloatingNav({
  items,
  activeId,
  trailingAction,
  className,
}: BottomFloatingNavProps) {
  const effectiveActiveId = useResolvedActiveId(items, activeId)

  return (
    <nav
      className={[styles.root, className].filter(Boolean).join(' ')}
      aria-label="Main"
    >
      <div className={styles.row}>
        <ul role="list" className={styles.pill}>
        {items.map((item) => {
          const isActive = item.id === effectiveActiveId
          // Extend the accessible name with unread state so screen readers
          // announce "Inbox, unread notifications" instead of just "Inbox".
          const ariaLabel = item.hasUnread
            ? `${item.label}, unread notifications`
            : item.label

          return (
            <li key={item.id} className={styles.tabItem}>
              <Link
                to={item.href}
                aria-label={ariaLabel}
                aria-current={isActive ? 'page' : undefined}
                className={[
                  styles.tab,
                  isActive ? styles.tabActive : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {/* Active background pill — fades + scales in via SCSS */}
                <span className={styles.tabBg} aria-hidden="true" />

                <span className={styles.tabIcon}>
                  <Icon name={item.icon} size={20} color="currentColor" />
                  {item.hasUnread && (
                    <span className={styles.unreadDot} aria-hidden="true" />
                  )}
                </span>

                {/* Animated label wrap — width 0 → auto + margin transition */}
                <span className={styles.tabLabelWrap}>
                  <Typography
                    variant="label-sm"
                    weight="medium"
                    color="inherit"
                    as="span"
                    className={styles.tabLabel}
                  >
                    {item.label}
                  </Typography>
                </span>
              </Link>
            </li>
          )
        })}
        </ul>
        {trailingAction ? (
          <div className={styles.trailingAction}>{trailingAction}</div>
        ) : null}
      </div>
    </nav>
  )
}
